import {
  Amount,
  BondPriceNominal,
  CorporateBondId,
  CouponPayment,
  IssuerId,
  MoodysRating,
  ProfitAndLoss,
  SpRating,
} from "@goodfill/domain-properties";
import { DateRange } from "../../../../filters/DateTimeRangeFilter";
import { FilterFunction } from "../../../../filters/filters";
import { isinFilter, ISIN_CRITERIA_KEY } from "../../../DataGridFilteredColumns/bond/IsinColumnDefinition";
import {
  issuerIdFilter,
  ISSUER_ID_CRITERIA_KEY,
} from "../../../DataGridFilteredColumns/bond/IssuerIdColumnDefinition";
import { MATURITY_CRITERIA_KEY } from "../../../DataGridFilteredColumns/bond/MaturityDateColumnDefinition";
import {
  MOODYS_RATING_CRITERIA_KEY,
  moodysRatingFilter,
} from "../../../DataGridFilteredColumns/bond/MoodysRatingColumnDefinition";
import {
  PAYMENT_RANK_CRITERIA_KEY,
  paymentRankFilter,
} from "../../../DataGridFilteredColumns/bond/PaymentRankColumnDefinition";
import {
  SP_RATING_CRITERIA_KEY,
  spRatingFilter,
} from "../../../DataGridFilteredColumns/bond/SpRatingColumnDefinition";
import { TICKER_CRITERIA_KEY, tickersFilter } from "../../../DataGridFilteredColumns/bond/TickerColumnDefinition";
import { PresentedPortfolioPosition } from "./PresentedPortfolioPosition";
import { Range } from "../../../DataGridFilteredColumns/generic/RangeColumnDefinition";
import {
  COUPON_PAYMENT_CRITERIA_KEY,
  couponPaymentFilter,
} from "../../../DataGridFilteredColumns/bond/CouponPaymentColumnDefinition";

export interface PortfolioPositionsFilter {
  tradeDateRange: DateRange;
  yearsHeldRange: [number | null, number | null];
  [TICKER_CRITERIA_KEY]: string[];
  [ISSUER_ID_CRITERIA_KEY]: IssuerId[];
  [PAYMENT_RANK_CRITERIA_KEY]: string[];
  [ISIN_CRITERIA_KEY]: CorporateBondId[];
  [MATURITY_CRITERIA_KEY]: DateRange;
  yearsToMaturityRange: [number | null, number | null];
  lockedInOrdersRange: [Amount | null, Amount | null];
  availableToSellRange: [Amount | null, Amount | null];
  loadedBuyPriceRange: [BondPriceNominal | null, BondPriceNominal | null];
  currentLoadedBidPriceRange: [BondPriceNominal | null, BondPriceNominal | null];

  pnlRealizedRange: [ProfitAndLoss | null, ProfitAndLoss | null];
  plnUnrealizedInOrdersRange: [ProfitAndLoss | null, ProfitAndLoss | null];
  plnUnrealizedAvailableToSellRange: [ProfitAndLoss | null, ProfitAndLoss | null];

  [MOODYS_RATING_CRITERIA_KEY]: Range<MoodysRating>;
  [SP_RATING_CRITERIA_KEY]: Range<SpRating>;
  [COUPON_PAYMENT_CRITERIA_KEY]: CouponPayment[];
}

export const defaultPortfolioPositionsFilterConstraints: PortfolioPositionsFilter = {
  tradeDateRange: [null, null],
  yearsHeldRange: [null, null],
  [TICKER_CRITERIA_KEY]: [],
  [ISSUER_ID_CRITERIA_KEY]: [],
  [PAYMENT_RANK_CRITERIA_KEY]: [],
  [ISIN_CRITERIA_KEY]: [],
  [MATURITY_CRITERIA_KEY]: [null, null],
  yearsToMaturityRange: [null, null],
  lockedInOrdersRange: [null, null],
  availableToSellRange: [null, null],
  loadedBuyPriceRange: [null, null],
  currentLoadedBidPriceRange: [null, null],
  pnlRealizedRange: [null, null],
  plnUnrealizedInOrdersRange: [null, null],
  plnUnrealizedAvailableToSellRange: [null, null],
  [MOODYS_RATING_CRITERIA_KEY]: [null, null],
  [SP_RATING_CRITERIA_KEY]: [null, null],
  [COUPON_PAYMENT_CRITERIA_KEY]: [],
};

export const portfolioPositionsFilters: FilterFunction<PresentedPortfolioPosition, PortfolioPositionsFilter>[] = [
  // Trade Date
  (item, constrains) => constrains.tradeDateRange[0] === null || constrains.tradeDateRange[0] <= item.tradeDate,
  (item, constrains) => constrains.tradeDateRange[1] === null || constrains.tradeDateRange[1] >= item.tradeDate,

  // Years held
  (item, constrains) => constrains.yearsHeldRange[0] === null || constrains.yearsHeldRange[0] <= item.yearsHeld,
  (item, constrains) => constrains.yearsHeldRange[1] === null || constrains.yearsHeldRange[1] >= item.yearsHeld,

  // Other
  tickersFilter("ticker"),
  issuerIdFilter("issuerId"),
  paymentRankFilter("paymentRank"),
  isinFilter("isin"),
  couponPaymentFilter("couponPayment"),

  // Maturity
  (item, constrains) =>
    constrains.maturityDateRange[0] === null || constrains.maturityDateRange[0] <= item.maturityDate,
  (item, constrains) =>
    constrains.maturityDateRange[1] === null || constrains.maturityDateRange[1] >= item.maturityDate,

  // Years held
  (item, constrains) =>
    constrains.yearsToMaturityRange[0] === null || constrains.yearsToMaturityRange[0] <= item.yearsToMaturity,
  (item, constrains) =>
    constrains.yearsToMaturityRange[1] === null || constrains.yearsToMaturityRange[1] >= item.yearsToMaturity,

  // Locked in Orders
  (item, constrains) =>
    constrains.lockedInOrdersRange[0] === null ||
    constrains.lockedInOrdersRange[0].lessThanOrEqualTo(item.lockedInOrders),
  (item, constrains) =>
    constrains.lockedInOrdersRange[1] === null ||
    constrains.lockedInOrdersRange[1].greaterThanOrEqualTo(item.lockedInOrders),

  // Available to sell
  (item, constrains) =>
    constrains.availableToSellRange[0] === null ||
    constrains.availableToSellRange[0].lessThanOrEqualTo(item.availableToSell),
  (item, constrains) =>
    constrains.availableToSellRange[1] === null ||
    constrains.availableToSellRange[1].greaterThanOrEqualTo(item.availableToSell),

  // Loaded Buy Price
  (item, constrains) =>
    constrains.loadedBuyPriceRange[0] === null ||
    constrains.loadedBuyPriceRange[0].lessThanOrEqualTo(item.loadedBuyPrice),
  (item, constrains) =>
    constrains.loadedBuyPriceRange[1] === null ||
    constrains.loadedBuyPriceRange[1].greaterThanOrEqualTo(item.loadedBuyPrice),

  // Current Loaded Bid Price
  (item, constrains) =>
    constrains.currentLoadedBidPriceRange[0] === null ||
    constrains.currentLoadedBidPriceRange[0].lessThanOrEqualTo(item.currentLoadedBidPrice),
  (item, constrains) =>
    constrains.currentLoadedBidPriceRange[1] === null ||
    constrains.currentLoadedBidPriceRange[1].greaterThanOrEqualTo(item.currentLoadedBidPrice),

  // P&L Realized
  (item, constrains) =>
    constrains.pnlRealizedRange[0] === null ||
    constrains.pnlRealizedRange[0].amount.lessThanOrEqualTo(item.pnlRealized.amount),
  (item, constrains) =>
    constrains.pnlRealizedRange[1] === null ||
    constrains.pnlRealizedRange[1].amount.greaterThanOrEqualTo(item.pnlRealized.amount),

  // P&L Unrealized in Orders
  (item, constrains) =>
    constrains.plnUnrealizedInOrdersRange[0] === null ||
    constrains.plnUnrealizedInOrdersRange[0].amount.lessThanOrEqualTo(item.plnUnrealizedInOrders.amount),
  (item, constrains) =>
    constrains.plnUnrealizedInOrdersRange[1] === null ||
    constrains.plnUnrealizedInOrdersRange[1].amount.greaterThanOrEqualTo(item.plnUnrealizedInOrders.amount),

  // P&L Unrealized in Orders
  (item, constrains) =>
    constrains.plnUnrealizedAvailableToSellRange[0] === null ||
    constrains.plnUnrealizedAvailableToSellRange[0].amount.lessThanOrEqualTo(item.plnUnrealizedAvailableToSell.amount),
  (item, constrains) =>
    constrains.plnUnrealizedAvailableToSellRange[1] === null ||
    constrains.plnUnrealizedAvailableToSellRange[1].amount.greaterThanOrEqualTo(
      item.plnUnrealizedAvailableToSell.amount,
    ),

  // Rating
  spRatingFilter("bondRatingSp"),
  moodysRatingFilter("bondRatingMoodys"),
];
